import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Trash2, Plus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  SingleSelect,
  Badge,
} from 'lift-ui';
import { UserGroup, Product } from '../../types';
import '../../iam-support.styles.scss';

interface PermissionRow {
  productKey: string;
  productName: string;
  levelName: string;
}

interface AssignGroupPermissionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: UserGroup | null;
  products: Product[];
  permissionLevels: string[];
  onSave: (groupId: number, permissions: Array<{ productId: number; levelName: string }>) => void;
  isSubmitting?: boolean;
}

const AssignGroupPermissionSheet: React.FC<AssignGroupPermissionSheetProps> = ({
  open,
  onOpenChange,
  group,
  products,
  permissionLevels,
  onSave,
  isSubmitting = false,
}) => {
  const [rows, setRows] = useState<PermissionRow[]>([]);
  const [draftProduct, setDraftProduct] = useState('');
  const [draftLevel, setDraftLevel] = useState('Viewer');

  // Seed rows from group's existing permissions when the sheet opens
  useEffect(() => {
    if (open && group) {
      const initial: PermissionRow[] = Object.entries(group.permissions || {}).map(
        ([productKey, levelName]) => ({
          productKey,
          productName: products.find(p => p.product_key === productKey)?.product_name || productKey,
          levelName,
        })
      );
      setRows(initial);
      setDraftProduct('');
      setDraftLevel('Viewer');
    }
  }, [open, group, products]);

  const originalKeys = useMemo(() => {
    if (!group) return new Set<string>();
    return new Set(Object.keys(group.permissions || {}));
  }, [group]);

  const hasChanges = useMemo(() => {
    const currentMap = new Map(rows.map(r => [r.productKey, r.levelName]));
    if (currentMap.size !== originalKeys.size) return true;
    for (const key of originalKeys) {
      if (!currentMap.has(key)) return true;
      if (currentMap.get(key) !== group?.permissions[key]) return true;
    }
    return false;
  }, [rows, originalKeys, group]);

  // Products already in rows — exclude from the "Add" dropdown
  const usedProductKeys = useMemo(() => new Set(rows.map(r => r.productKey)), [rows]);

  const availableProducts = useMemo(
    () => products.filter(p => !usedProductKeys.has(p.product_key)),
    [products, usedProductKeys]
  );

  const handleAddRow = useCallback(() => {
    if (!draftProduct) return;
    const product = products.find(p => p.product_name === draftProduct);
    if (!product) return;
    setRows(prev => [
      ...prev,
      { productKey: product.product_key, productName: product.product_name, levelName: draftLevel },
    ]);
    setDraftProduct('');
    setDraftLevel('Viewer');
  }, [draftProduct, draftLevel, products]);

  const handleRemoveRow = useCallback((productKey: string) => {
    setRows(prev => prev.filter(r => r.productKey !== productKey));
  }, []);

  const handleLevelChange = useCallback((productKey: string, newLevel: string) => {
    setRows(prev => prev.map(r => (r.productKey === productKey ? { ...r, levelName: newLevel } : r)));
  }, []);

  const handleSave = useCallback(() => {
    if (!group || rows.length === 0) return;
    const payload = rows.map(r => {
      const product = products.find(p => p.product_key === r.productKey);
      return { productId: product!.product_id, levelName: r.levelName };
    });
    onSave(group.id, payload);
  }, [group, rows, products, onSave]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const getLevelVariant = (level: string): 'default' | 'secondary' | 'outline' => {
    switch (level.toLowerCase()) {
      case 'manager': return 'default';
      case 'editor': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg" className="iam-support-sheet flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Manage Permissions — {group?.name}</SheetTitle>
          <SheetDescription>
            Add or remove product permissions. Changes take effect when you save.
          </SheetDescription>
        </SheetHeader>

        <div className="iam-support-dialog-form flex-1 overflow-y-auto pt-2">
          {/* Add row form */}
          {availableProducts.length > 0 && (
            <div className="mb-6">
              <Label className="text-sm font-medium block mb-3">Add Permission</Label>
              <div className="flex items-end gap-2">
                <div className="iam-support-form-field flex-1 min-w-0">
                  <Label className="text-xs text-muted-foreground">Product</Label>
                  <SingleSelect
                    options={availableProducts.map(p => ({ value: p.product_name, label: p.product_name }))}
                    defaultValue={draftProduct || undefined}
                    onChange={(val) => setDraftProduct(val || '')}
                    placeholder="Select product"
                    containerClassName="w-full"
                  />
                </div>
                <div className="iam-support-form-field shrink-0 w-[140px]">
                  <Label className="text-xs text-muted-foreground">Level</Label>
                  <SingleSelect
                    options={permissionLevels.map(l => ({ value: l, label: l }))}
                    defaultValue={draftLevel || undefined}
                    onChange={(val) => setDraftLevel(val || 'Viewer')}
                    containerClassName="w-full"
                  />
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddRow}
                  disabled={!draftProduct}
                  className="shrink-0 h-9"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          )}

          {/* Current permission rows */}
          {rows.length > 0 ? (
            <div className="border-t border-border pt-4">
              <Label className="text-sm font-medium mb-3 block">Permissions ({rows.length})</Label>
              <div className="space-y-2">
                {rows.map(row => (
                  <div
                    key={row.productKey}
                    className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border"
                  >
                    <span className="font-medium text-sm truncate min-w-0 flex-1">
                      {row.productName}
                    </span>
                    <div className="iam-support-form-field shrink-0 w-[140px]">
                      <SingleSelect
                        options={permissionLevels.map(l => ({ value: l, label: l }))}
                        defaultValue={row.levelName}
                        onChange={(val) => handleLevelChange(row.productKey, val || 'Viewer')}
                        containerClassName="w-full"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRow(row.productKey)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                No product permissions yet. Add one above.
              </p>
            </div>
          )}
        </div>

        <SheetFooter className="border-t border-border pt-4 mt-4 shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={rows.length === 0 || !hasChanges || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AssignGroupPermissionSheet;
